/**
 * AuthContext
 *
 * Global authentication state management using React Context API.
 * Provides user data, token, and authentication helper functions throughout the app.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { ACCOUNT_TYPES } from '../utils/appConstants';
import { supabase, getSupabaseSession, refreshSupabaseSession } from '../services/supabaseClient';
import { logout as signOutSupabase } from '../services/authService';

const AuthContext = createContext(null);

/**
 * AuthProvider Component
 *
 * Wraps the application to provide authentication state globally
 *
 * @param {Object} props
 * @param {ReactNode} props.children - Child components
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Initialize auth state from localStorage on mount
   */
  useEffect(() => {
    let unsubscribe = null;

    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('authToken');
      const storedRefreshToken = localStorage.getItem('refreshToken');
      const storedUser = localStorage.getItem('authUser');

      if (storedToken && storedUser) {
        try {
          setToken(storedToken);
          setRefreshToken(storedRefreshToken);
          setUser(JSON.parse(storedUser));
        } catch (error) {
          console.error('Error parsing stored user data:', error);
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('authUser');
        }
      }

      const session = await getSupabaseSession();
      if (session?.access_token) {
        setToken(session.access_token);
        setRefreshToken(session.refresh_token);
        localStorage.setItem('authToken', session.access_token);
        if (session.refresh_token) {
          localStorage.setItem('refreshToken', session.refresh_token);
        }
      }

      setIsLoading(false);
    };

    initializeAuth();

    if (supabase) {
      const { data } = supabase.auth.onAuthStateChange((_event, session) => {
        if (session?.access_token) {
          setToken(session.access_token);
          setRefreshToken(session.refresh_token);
          localStorage.setItem('authToken', session.access_token);
          if (session.refresh_token) {
            localStorage.setItem('refreshToken', session.refresh_token);
          }
        }
      });
      unsubscribe = () => data?.subscription?.unsubscribe();
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  /**
   * Login user and persist to localStorage
   *
   * @param {string} authToken - JWT access token
   * @param {Object} userData - User data object
   * @param {string} refreshTokenValue - JWT refresh token (optional)
   */
  const login = (authToken, userData, refreshTokenValue = null) => {
    setToken(authToken);
    setUser(userData);

    if (refreshTokenValue) {
      setRefreshToken(refreshTokenValue);
      localStorage.setItem('refreshToken', refreshTokenValue);
    }

    // Persist to localStorage
    localStorage.setItem('authToken', authToken);
    localStorage.setItem('authUser', JSON.stringify(userData));

    console.log('User logged in:', userData);
  };

  /**
   * Refresh access token using refresh token
   * @returns {Promise<string|null>} New access token or null if failed
   */
  const refreshAccessToken = async () => {
    try {
      const session = await refreshSupabaseSession();
      const newAccessToken = session?.access_token;

      if (!newAccessToken) {
        console.error('Token refresh failed');
        logout();
        return null;
      }

      setToken(newAccessToken);
      setRefreshToken(session.refresh_token);
      localStorage.setItem('authToken', newAccessToken);
      if (session.refresh_token) {
        localStorage.setItem('refreshToken', session.refresh_token);
      }

      console.log('✅ Access token refreshed successfully');
      return newAccessToken;
    } catch (error) {
      console.error('❌ Error refreshing token:', error);
      logout();
      return null;
    }
  };

  /**
   * Update user fields in context and localStorage
   * @param {Object} patch - Fields to merge into current user
   */
  const updateUser = (patch) => {
    setUser((prev) => {
      const updated = { ...prev, ...patch };
      localStorage.setItem('authUser', JSON.stringify(updated));
      return updated;
    });
  };

  /**
   * Logout user and clear localStorage
   */
  const logout = () => {
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    signOutSupabase().catch((error) => {
      console.error('Supabase logout failed:', error);
    });

    // Clear localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('authUser');
    localStorage.removeItem('token'); // Also clear old 'token' key if it exists

    console.log('User logged out');
  };

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  const isAuthenticated = () => {
    return !!token && !!user;
  };

  /**
   * Check if user is a werkgever (employer)
   * @returns {boolean}
   */
  const isWerkgever = () => {
    return user?.accountType === ACCOUNT_TYPES.EMPLOYER;
  };

  /**
   * Check if user is a student
   * @returns {boolean}
   */
  const isStudent = () => {
    return user?.accountType === ACCOUNT_TYPES.STUDENT;
  };

  /**
   * Check if user is an admin
   * @returns {boolean}
   */
  const isAdmin = () => {
    return user?.accountType === ACCOUNT_TYPES.ADMIN;
  };

  const value = {
    user,
    token,
    refreshToken,
    isLoading,
    login,
    logout,
    updateUser,
    refreshAccessToken,
    isAuthenticated,
    isWerkgever,
    isStudent,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * useAuth Hook
 *
 * Custom hook to access authentication context
 *
 * @returns {Object} Authentication context value
 * @throws {Error} If used outside of AuthProvider
 */
export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }

  return context;
};

export default AuthContext;
